import { orderService } from '../../../services/firebaseService';
import { transformPaymentPeriodLabel } from '../../../utils/orderUtils';

export const createOrder = async ({
  cart,
  paidItems,
  giftItems,
  customerData,
  selectedCurrency,
  selectedCountry,
  checkoutPaymentPeriod,
  installmentFrequency,
  installmentOverride,
  numberOfInstallments,
  getSelectedPaymentPeriod,
  orderPath,
  currentAgent,
  demoData,
  COUNTRIES,
  getCartTotal,
  getDiscountedCartTotal,
  discountAmt,
  clearCart,
  setCustomerData,
  reset,
  setStep,
  setLoading
}) => {
  if (paidItems.length === 0) {
    alert('Please add at least one paid item to create an order');
    return;
  }

  if (!customerData && demoData?.customer) {
    alert('Please review and confirm customer details');
    return;
  }

  if (!customerData) {
    alert('Please fill in customer details');
    return;
  }

  try {
    setLoading(true);
    
    const total = getDiscountedCartTotal();
    
    const totalUSD = cart.reduce((sum, item) => {
      if (!item.isGift) {
        const usdPrice = item.prices?.USD || item.price || 0;
        return sum + (usdPrice * (item.quantity || 0));
      }
      return sum;
    }, 0);

    const originalTotal = getCartTotal();
    const discountProportion = originalTotal > 0 ? (total / originalTotal) : 1;
    const finalTotalUSD = totalUSD * discountProportion;
    
    const referrer = demoData?.referrer || customerData?.referrer || null;
    
    const giftPaymentPeriod = getSelectedPaymentPeriod(giftItems);
    const finalPaymentPeriod = giftPaymentPeriod || checkoutPaymentPeriod;
    
    const periodDaysMap = { 'day1': 1, 'day30': 30, 'day60': 60, 'day90': 90, 'day180': 180 };
    const periodDays = periodDaysMap[finalPaymentPeriod] || 1;

    const calculatedPaymentPlan = finalPaymentPeriod !== 'day1'
      ? (() => {
          const today = new Date();
          const timestamp = today.toISOString().slice(0, 19).replace('T', ' ');
          const finalDate = new Date(today);
          finalDate.setDate(today.getDate() + periodDays);

          const finalInstallments = numberOfInstallments;
          const finalAmount = installmentOverride || Math.round(total / parseInt(finalInstallments || '1')).toFixed(0);

          const totalPlanned = parseFloat(finalAmount) * parseInt(finalInstallments);
          if (totalPlanned < total - 1) {
            console.warn(`Total planned payment (${totalPlanned}) is less than total due (${total})`);
          }

          return {
            ins_frequency: installmentFrequency,
            ins_amount: finalAmount,
            max_no_of_ins: finalInstallments,
            final_ins_date: finalDate.toISOString().slice(0, 10),
            timestamp: timestamp
          };
        })()
      : null;
    
    const agentCode = currentAgent?.code || currentAgent?.agent_id || currentAgent?.id || 'AGT';
    const orderData = {
      customer: customerData,
      items: cart.map(({ priceUSD, priceLocal, local_prices, ...item }) => ({
        ...item,
        price: parseFloat(item.price) || 0
      })),
      paidItemsCount: paidItems.length,
      giftItemsCount: giftItems.length,
      total: total,
      discountAmt: discountAmt || 0,
      currency: selectedCurrency,
      country: selectedCountry,
      paymentPeriod: finalPaymentPeriod,
      paymentPlan: calculatedPaymentPlan,
      agent: {
        id: currentAgent.id,
        name: currentAgent.name,
        email: currentAgent.email,
        code: agentCode
      },
      paymentMethod: customerData.paymentMethod,
      agentId: currentAgent.id,
      demoBookingId: customerData?.demoBookingId || null,
      referrer: referrer,
      createdAt: new Date().toISOString(),
      payment: (customerData.paymentMethod === 'cash' || customerData.paymentMethod === 'credit_card' || customerData.paymentMethod === 'debit_card') ? {
        method: customerData.paymentMethod === 'cash' ? 'Cash' : customerData.paymentMethod === 'credit_card' ? 'Credit Card' : 'Debit Card',
        reference: customerData.paymentReference,
        total_amount_usd: finalTotalUSD.toFixed(2),
        amount_paid_usd: customerData.amountPaidUSD,
        total_amount_local_currency: total.toFixed(2),
        amount_paid_local_currency: customerData.amountPaidLocal,
        currency: selectedCurrency,
        local_currency: COUNTRIES[selectedCountry]?.currency || selectedCurrency,
        status: customerData.paymentStatus
      } : null
    };
    
    Object.keys(orderData).forEach(key =>
      orderData[key] === undefined && delete orderData[key]
    );

    if (!currentAgent?.id) {
      throw new Error('No authenticated agent found');
    }

    let result;
    if (orderPath === 'draft') {
      result = await orderService.createDraftOrder(orderData, currentAgent.id);
    } else {
      result = await orderService.createOrder(orderData, currentAgent.id);
    }

    if (!result.success) {
      alert('Failed to create order. Please try again.');
    }
    return result;
  } catch (error) {
    console.error('Error creating order:', error);
    alert('Failed to create order. Please try again.');
  } finally {
    setLoading(false);
  }
};
